import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './UserProfile.css';

interface UserProfileProps {
  onLogout?: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onLogout }) => {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // 重置图片加载错误状态当用户改变时
  useEffect(() => {
    setImageLoadError(false);
  }, [user?.picture]);

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    onLogout?.();
    setIsMenuOpen(false);
  };

  // 处理头像图片加载错误
  const handleImageError = () => {
    setImageLoadError(true);
  };

  // 检查是否应该显示Google头像
  const shouldShowGoogleAvatar = () => {
    return user?.picture && user?.isGoogleUser && !imageLoadError;
  };

  // 生成默认头像（使用英文名首字母）
  const getDefaultAvatar = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 获取显示名称
  const getDisplayName = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name;
    }
    return user?.displayName || user?.name || user?.email?.split('@')[0] || '用户';
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="header-profile group" data-header-menu="profile-menu" ref={menuRef}>
      {/* 桌面端菜单按钮 */}
      <button
        aria-haspopup="menu"
        aria-controls="profile-menu"
        type="button"
        data-header-menu-trigger="true"
        className="profile-menu-trigger hidden md:grid"
        onClick={toggleMenu}
        title={`${getDisplayName()} (Credits: ${user.credits})`}
      >
        <div className="avatar-container">
          {shouldShowGoogleAvatar() ? (
            <img
              alt="user profile"
              src={user.picture}
              className="avatar-image"
              onError={handleImageError}
              onLoad={() => setImageLoadError(false)}
            />
          ) : (
            <div className="avatar-placeholder">
              {getDefaultAvatar()}
            </div>
          )}
        </div>
      </button>

      {/* 移动端链接 */}
      <button
        className="profile-link md:hidden"
        onClick={toggleMenu}
        title={`${getDisplayName()} (Credits: ${user.credits})`}
      >
        <div className="avatar-container">
          {shouldShowGoogleAvatar() ? (
            <img
              alt="user profile"
              src={user.picture}
              className="avatar-image"
              onError={handleImageError}
              onLoad={() => setImageLoadError(false)}
            />
          ) : (
            <div className="avatar-placeholder">
              {getDefaultAvatar()}
            </div>
          )}
        </div>
      </button>

      {/* 下拉菜单 */}
      <div
        id="profile-menu"
        role="menu"
        aria-orientation="vertical"
        className={`profile-dropdown ${isMenuOpen ? 'menu-active' : ''}`}
        style={{ opacity: isMenuOpen ? 1 : 0 }}
        data-menu-active={isMenuOpen}
      >
        <div role="presentation" className="dropdown-content">
          {/* Credits Display */}
          <div role="menuitem" className="menu-item credits-item">
            <div className="credits-progress">
              <p className="credits-text">
                <span>{user.displayName || user.name || 'User'}</span>
              </p>
              <p className="credits-amount">Credits: {user.credits}</p>
            </div>
          </div>

          {/* Settings Button */}
          <button
            role="menuitem"
            className="menu-item settings-item"
            onClick={() => {
              navigate('/settings');
              setIsMenuOpen(false);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path
                d="M5.7936 1.93871C6.24365 -0.548298 10.0098 -0.465039 10.2389 2.18773L10.2604 2.33714C10.4161 3.06464 11.2184 3.48163 11.9157 3.1555L12.1481 3.0598C14.4502 2.23359 16.2209 5.30054 14.3541 6.88109L14.1549 7.03343C13.4818 7.50348 13.482 8.50069 14.1549 8.97093L14.3541 9.12328C16.221 10.7038 14.4503 13.7708 12.1481 12.9446L11.9157 12.8489C11.2186 12.5231 10.4163 12.9401 10.2604 13.6672L10.2389 13.8166C10.0102 16.47 6.24336 16.5532 5.7936 14.0657L5.76039 13.8166C5.69385 13.0502 4.93191 12.5644 4.22426 12.7932L4.08364 12.8489C1.59375 14.0137 -0.40919 10.5449 1.84438 8.97093L1.96254 8.87621C2.47791 8.41084 2.47795 7.59349 1.96254 7.12816L1.84438 7.03343C-0.408466 5.45942 1.59404 1.99115 4.08364 3.1555L4.22426 3.21117C4.93205 3.43997 5.69411 2.95447 5.76039 2.18773L5.7936 1.93871ZM9.17641 2.27855C9.05543 0.884691 7.07686 0.841389 6.84047 2.14867L6.82289 2.27855C6.69294 3.78616 5.14802 4.7234 3.76528 4.17894L3.63149 4.12132C2.32307 3.50978 1.27103 5.332 2.45473 6.15941L2.57094 6.24632C3.69687 7.14166 3.69684 8.86269 2.57094 9.75804L2.45473 9.84496C1.27031 10.6723 2.32278 12.4951 3.63149 11.883L3.76528 11.8254C5.14789 11.281 6.69268 12.2185 6.82289 13.7258L6.84047 13.8557C7.07658 15.1635 9.05581 15.1203 9.17641 13.7258L9.19301 13.5813C9.41279 12.1116 10.9973 11.2421 12.3678 11.883L12.4899 11.9338C13.6999 12.3678 14.6303 10.7558 13.6491 9.92503L13.5446 9.84496C12.2642 8.95046 12.2639 7.0537 13.5446 6.15941L13.6491 6.07933C14.6303 5.24857 13.6999 3.6366 12.4899 4.07054L12.3678 4.12132C10.9971 4.7626 9.41259 3.89307 9.19301 2.42308L9.17641 2.27855ZM7.99965 5.46703C9.39857 5.46703 10.5329 6.60131 10.5329 8.00023C10.5327 9.39903 9.39849 10.5334 7.99965 10.5334C6.60093 10.5333 5.46658 9.39895 5.46645 8.00023C5.46645 6.6014 6.60085 5.46717 7.99965 5.46703ZM7.99965 6.53343C7.18955 6.53357 6.53285 7.19009 6.53285 8.00023C6.53299 8.81025 7.18963 9.46689 7.99965 9.46703C8.80979 9.46703 9.46632 8.81034 9.46645 8.00023C9.46645 7.19001 8.80987 6.53343 7.99965 6.53343Z"
                fill="currentColor"
              />
            </svg>
            Settings
          </button>

          {/* Logout Button */}
          <button
            role="menuitem"
            className="menu-item logout-item"
            onClick={handleLogout}
          >
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path
                d="M6.33305 13.167C6.33287 13.443 6.10908 13.667 5.83305 13.667H3.83305C2.82068 13.6668 2.00004 12.8454 2.00004 11.833V3.83301C2.00022 2.82075 2.82079 2.00018 3.83305 2H5.83305C6.10918 2.00001 6.33305 2.22387 6.33305 2.5C6.33305 2.77614 6.10919 2.99999 5.83305 3H3.83305C3.37307 3.00018 3.00022 3.37303 3.00004 3.83301V11.833C3.00004 12.2931 3.37296 12.6668 3.83305 12.667H5.83305C6.10918 12.667 6.33305 12.8909 6.33305 13.167ZM13.918 7.55859C14.0458 7.75256 14.0241 8.01579 13.8536 8.18652L11.1866 10.8535L11.1084 10.918C10.9144 11.046 10.6503 11.0243 10.4795 10.8535C10.3087 10.6826 10.2877 10.4187 10.4161 10.2246L10.4795 10.1465L12.293 8.33301H6.50004C6.2239 8.33301 6.00004 8.10915 6.00004 7.83301C6.00022 7.55702 6.22401 7.33301 6.50004 7.33301H12.293L10.4795 5.52051C10.2844 5.32535 10.2846 5.00876 10.4795 4.81348C10.6748 4.61823 10.9913 4.61822 11.1866 4.81348L13.8536 7.47949L13.918 7.55859Z"
                fill="currentColor"
              />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
